import { useEffect, useState } from 'react';
import { X, Share, Plus, Bell, CheckCircle2 } from 'lucide-react';

/**
 * iOS PWA install/notification helper.
 * On iOS Safari, shows a small banner explaining Add-to-Home-Screen requirement.
 * Hides itself once installed (standalone mode) OR when user dismisses.
 */
export default function IosPushHelp() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Detect iOS
    const ua = navigator.userAgent || '';
    const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    if (!isIos) return;
    // Check if already running as installed PWA
    const isStandalone = window.navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches;
    if (isStandalone) return;
    // Check if dismissed recently
    const dismissed = localStorage.getItem('ptt_ios_push_dismissed');
    if (dismissed && Date.now() - parseInt(dismissed, 10) < 7 * 24 * 60 * 60 * 1000) return;
    setShow(true);
  }, []);

  if (!show) return null;

  const dismiss = () => {
    localStorage.setItem('ptt_ios_push_dismissed', String(Date.now()));
    setShow(false);
  };

  return (
    <div className="fixed inset-x-3 bottom-3 sm:right-4 sm:left-auto sm:max-w-sm z-50 bg-white rounded-2xl shadow-2xl border border-teal-200 overflow-hidden" data-testid="ios-push-help">
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-teal-500/15 text-teal-700 flex items-center justify-center flex-shrink-0">
            <Bell className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-sm">Get push alerts on iPhone</div>
            <div className="text-xs text-zinc-600 mt-0.5">Add PTT NZ Alerts to your Home Screen first — then enable notifications from inside the app.</div>
          </div>
          <button onClick={dismiss} className="text-zinc-400 hover:text-zinc-700 flex-shrink-0" data-testid="ios-push-dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>
        <ol className="mt-3 space-y-1.5 text-xs text-zinc-700 pl-1">
          <li className="flex items-start gap-2">
            <span className="chip chip-outline-orange" style={{ padding: '1px 6px', fontSize: '9px' }}>1</span>
            Tap the <Share className="w-3.5 h-3.5 inline mx-0.5" /> <strong>Share</strong> button in Safari
          </li>
          <li className="flex items-start gap-2">
            <span className="chip chip-outline-orange" style={{ padding: '1px 6px', fontSize: '9px' }}>2</span>
            Tap <Plus className="w-3.5 h-3.5 inline mx-0.5" /> <strong>Add to Home Screen</strong>
          </li>
          <li className="flex items-start gap-2">
            <span className="chip chip-outline-orange" style={{ padding: '1px 6px', fontSize: '9px' }}>3</span>
            Open PTT NZ Alerts from your Home Screen → Settings → enable Web Push
          </li>
          <li className="flex items-start gap-2 text-teal-700 font-semibold pt-1">
            <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            Requires iOS 16.4 or later
          </li>
        </ol>
      </div>
    </div>
  );
}
